import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import {
  ConfirmDialog,
  EmptyState,
  FormField,
  LoadingState,
  Modal,
  Toast,
} from './ui';

const WeightTablesList = ({ beltId }) => {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableDialogOpen, setTableDialogOpen] = useState(false);
  const [rowDialogOpen, setRowDialogOpen] = useState(false);
  const [selectedTable, setSelectedTable] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [currentTableId, setCurrentTableId] = useState(null);
  const [confirmTableId, setConfirmTableId] = useState(null);
  const [confirmRow, setConfirmRow] = useState(null);
  const [tableFormData, setTableFormData] = useState({
    title: '',
    column_boys: 'NIÑOS',
    column_girls: 'NIÑAS',
  });
  const [rowFormData, setRowFormData] = useState({ weight_boys: '', weight_girls: '' });
  const [toast, setToast] = useState({ open: false, message: '', tone: 'success' });

  const showToast = (message, tone = 'success') => setToast({ open: true, message, tone });

  const fetchTables = async () => {
    try {
      const response = await api.get(`/admin/belts/${beltId}/weight-tables`);
      setTables(response.data.data);
    } catch (error) {
      showToast('Error al cargar tablas', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTables();
  }, [beltId]);

  const openTableDialog = (table = null) => {
    setSelectedTable(table);
    setTableFormData(
      table
        ? {
            title: table.title || '',
            column_boys: table.column_boys || 'NIÑOS',
            column_girls: table.column_girls || 'NIÑAS',
          }
        : { title: '', column_boys: 'NIÑOS', column_girls: 'NIÑAS' }
    );
    setTableDialogOpen(true);
  };

  const openRowDialog = (tableId, row = null) => {
    setCurrentTableId(tableId);
    setSelectedRow(row);
    setRowFormData(
      row
        ? { weight_boys: row.weight_boys || '', weight_girls: row.weight_girls || '' }
        : { weight_boys: '', weight_girls: '' }
    );
    setRowDialogOpen(true);
  };

  const handleTableSubmit = async (e) => {
    e.preventDefault();
    if (!tableFormData.title.trim()) {
      showToast('El título es obligatorio', 'error');
      return;
    }
    try {
      if (selectedTable) {
        await api.put(`/admin/belts/${beltId}/weight-tables/${selectedTable.id}`, tableFormData);
        showToast('Tabla actualizada');
      } else {
        await api.post(`/admin/belts/${beltId}/weight-tables`, tableFormData);
        showToast('Tabla creada');
      }
      setTableDialogOpen(false);
      fetchTables();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar tabla', 'error');
    }
  };

  const handleRowSubmit = async (e) => {
    e.preventDefault();
    if (!rowFormData.weight_boys.trim() || !rowFormData.weight_girls.trim()) {
      showToast('Ambos pesos son obligatorios', 'error');
      return;
    }
    try {
      if (selectedRow) {
        await api.put(
          `/admin/belts/${beltId}/weight-tables/${currentTableId}/rows/${selectedRow.id}`,
          rowFormData
        );
        showToast('Fila actualizada');
      } else {
        await api.post(
          `/admin/belts/${beltId}/weight-tables/${currentTableId}/rows`,
          rowFormData
        );
        showToast('Fila creada');
      }
      setRowDialogOpen(false);
      fetchTables();
    } catch (error) {
      showToast(error.response?.data?.message || 'Error al guardar fila', 'error');
    }
  };

  const deleteTable = async () => {
    try {
      await api.delete(`/admin/belts/${beltId}/weight-tables/${confirmTableId}`);
      setConfirmTableId(null);
      showToast('Tabla eliminada');
      fetchTables();
    } catch (error) {
      showToast('Error al eliminar tabla', 'error');
    }
  };

  const deleteRow = async () => {
    try {
      await api.delete(
        `/admin/belts/${beltId}/weight-tables/${confirmRow.tableId}/rows/${confirmRow.rowId}`
      );
      setConfirmRow(null);
      showToast('Fila eliminada');
      fetchTables();
    } catch (error) {
      showToast('Error al eliminar fila', 'error');
    }
  };

  const moveTable = async (id, dir) => {
    try {
      await api.put(`/admin/belts/${beltId}/weight-tables/${id}/move-${dir}`);
      fetchTables();
    } catch (error) {
      showToast('Error al mover tabla', 'error');
    }
  };

  const moveRow = async (tableId, rowId, dir) => {
    try {
      await api.put(`/admin/belts/${beltId}/weight-tables/${tableId}/rows/${rowId}/move-${dir}`);
      fetchTables();
    } catch (error) {
      showToast('Error al mover fila', 'error');
    }
  };

  if (loading) return <LoadingState label="Cargando tablas…" />;

  return (
    <div>
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="text-lg font-semibold">Tablas de peso</h2>
        <button type="button" className="btn-admin" onClick={() => openTableDialog()}>
          Agregar tabla
        </button>
      </div>

      {tables.length === 0 ? (
        <EmptyState title="Sin tablas de peso" />
      ) : (
        <ul className="space-y-6">
          {tables.map((table, tIndex) => (
            <li key={table.id} className="rounded-lg border border-line bg-surface p-4">
              <div className="mb-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                <h3 className="text-center font-bold uppercase tracking-wide text-primary sm:text-left">
                  {table.title}
                </h3>
                <div className="flex flex-wrap gap-1">
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={tIndex === 0} onClick={() => moveTable(table.id, 'up')}>↑</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" disabled={tIndex === tables.length - 1} onClick={() => moveTable(table.id, 'down')}>↓</button>
                  <button type="button" className="btn-admin-ghost !min-h-9" onClick={() => openTableDialog(table)}>Editar</button>
                  <button type="button" className="btn-admin-secondary !min-h-9" onClick={() => openRowDialog(table.id)}>Fila</button>
                  <button type="button" className="btn-admin-ghost !min-h-9 text-danger" onClick={() => setConfirmTableId(table.id)}>Eliminar</button>
                </div>
              </div>

              <div className="overflow-x-auto rounded-md border border-line">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-canvas">
                      <th className="px-3 py-2 text-center font-semibold">{table.column_boys}</th>
                      <th className="px-3 py-2 text-center font-semibold">{table.column_girls}</th>
                      <th className="px-3 py-2 text-right font-semibold">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.rows?.length ? (
                      table.rows.map((row, rIndex) => (
                        <tr key={row.id} className="odd:bg-surface even:bg-canvas/50">
                          <td className="px-3 py-2 text-center font-medium">{row.weight_boys}</td>
                          <td className="px-3 py-2 text-center font-medium">{row.weight_girls}</td>
                          <td className="px-3 py-2">
                            <div className="flex justify-end gap-1">
                              <button type="button" className="btn-admin-ghost !min-h-8 !px-2" disabled={rIndex === 0} onClick={() => moveRow(table.id, row.id, 'up')}>↑</button>
                              <button type="button" className="btn-admin-ghost !min-h-8 !px-2" disabled={rIndex === table.rows.length - 1} onClick={() => moveRow(table.id, row.id, 'down')}>↓</button>
                              <button type="button" className="btn-admin-ghost !min-h-8 !px-2" onClick={() => openRowDialog(table.id, row)}>Editar</button>
                              <button type="button" className="btn-admin-ghost !min-h-8 !px-2 text-danger" onClick={() => setConfirmRow({ tableId: table.id, rowId: row.id })}>✕</button>
                            </div>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={3} className="px-3 py-6 text-center text-muted">
                          Sin filas
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </li>
          ))}
        </ul>
      )}

      <Modal
        open={tableDialogOpen}
        title={selectedTable ? 'Editar tabla' : 'Agregar tabla'}
        onClose={() => setTableDialogOpen(false)}
        footer={
          <>
            <button type="button" className="btn-admin-secondary" onClick={() => setTableDialogOpen(false)}>Cancelar</button>
            <button type="submit" form="table-form" className="btn-admin">{selectedTable ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="table-form" className="space-y-4" onSubmit={handleTableSubmit}>
          <FormField id="title" label="Título" value={tableFormData.title} onChange={(e) => setTableFormData((p) => ({ ...p, title: e.target.value }))} required />
          <FormField id="column_boys" label="Columna 1" value={tableFormData.column_boys} onChange={(e) => setTableFormData((p) => ({ ...p, column_boys: e.target.value }))} />
          <FormField id="column_girls" label="Columna 2" value={tableFormData.column_girls} onChange={(e) => setTableFormData((p) => ({ ...p, column_girls: e.target.value }))} />
        </form>
      </Modal>

      <Modal
        open={rowDialogOpen}
        title={selectedRow ? 'Editar fila' : 'Agregar fila'}
        onClose={() => setRowDialogOpen(false)}
        footer={
          <>
            <button type="button" className="btn-admin-secondary" onClick={() => setRowDialogOpen(false)}>Cancelar</button>
            <button type="submit" form="row-form" className="btn-admin">{selectedRow ? 'Actualizar' : 'Crear'}</button>
          </>
        }
      >
        <form id="row-form" className="space-y-4" onSubmit={handleRowSubmit}>
          <FormField id="weight_boys" label="Peso columna 1" value={rowFormData.weight_boys} onChange={(e) => setRowFormData((p) => ({ ...p, weight_boys: e.target.value }))} required />
          <FormField id="weight_girls" label="Peso columna 2" value={rowFormData.weight_girls} onChange={(e) => setRowFormData((p) => ({ ...p, weight_girls: e.target.value }))} required />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmTableId)}
        title="Eliminar tabla"
        message="¿Eliminar esta tabla y todas sus filas?"
        confirmLabel="Eliminar"
        danger
        onConfirm={deleteTable}
        onClose={() => setConfirmTableId(null)}
      />

      <ConfirmDialog
        open={Boolean(confirmRow)}
        title="Eliminar fila"
        message="¿Eliminar esta fila?"
        confirmLabel="Eliminar"
        danger
        onConfirm={deleteRow}
        onClose={() => setConfirmRow(null)}
      />

      <Toast open={toast.open} message={toast.message} tone={toast.tone} onClose={() => setToast((t) => ({ ...t, open: false }))} />
    </div>
  );
};

export default WeightTablesList;
